import {PlainObject} from '@xh/hoist/core';
import {IGraphElement} from '../cmp/cytoGraph/CytoGraphTypes';

export class Roles {
    static ADMIN = 'ADMIN';
    static SEC_MASTER_MANAGER = 'SEC_MASTER_MANAGER';
    static WORKFLOW_MANAGER = 'WORKFLOW_MANAGER';
}

export enum ManagementTypeEnum {
    Managed = 'Managed',
    Direct = 'Direct'
}

export enum TaxTreatmentEnum {
    Taxable = 'Taxable',
    TaxDeferred = 'TaxDeferred',
    TaxFree = 'TaxFree'
}

export enum HoldingsTypeEnum {
    Cash,
    Investment,
    Spec,
    Hedge,
    Loan
}

export enum SecurityTypeEnum {
    EQUITY = 'Equity',
    FX = 'FX',
    CASH = 'Cash',
    BOND = 'Bond',
    REAL_ESTATE = 'RealEstate',
    LOANS = 'Loan',
    FUTURE = 'Future'
}

export enum SecuritySubTypeEnum {
    STOCK = 'Stock',
    ETF = 'ETF',
    MUTUAL_FUND = 'MutualFund',
    ADR = 'ADR',
    CASH = 'Cash',
    OPTIONS = 'Option',
    LOAN = 'Loan',
    LLC = 'LLC',
    PERSONAL = 'Personal',
    MONEY_MARKET = 'MoneyMarket',
    CD = 'CD',
    TREASURY = 'Treasury',
    MUNI = 'Muni',
    CORP = 'Corp',
    ANNUITY = 'Annuity',
    INDEX = 'Index',
    COMMODITY = 'Commodity',
    EQUITY = 'Equity',
    RATES = 'Rates',
    SPOT = 'Spot',
    CRYPTO = 'Crypto'
}

export type Portfolio = {
    id: number;
    userId: number;
    portfolioName: string;
    portfolioId?: string;
    brokerage?: string;
    isActive?: boolean;
    managementType?: ManagementTypeEnum;
    holdingsType?: HoldingsTypeEnum;
    taxableType?: TaxTreatmentEnum;
    updated?: string;
    updatedBy?: string;
    timestamp?: any;
};

export type PortfolioHistory = {
    asOfDate: string;
    totalBalance: number;
};

export type Position = {
    id: string;
    positionKey: PositionKey;
    portfolioId: number;
    securityId: number;
    portfolioHoldingType: HoldingsTypeEnum;
    portfolioTaxType: TaxTreatmentEnum;
    /** Added to the cube records by LivePositionsService. */
    portfolioName?: string;
    ticker: string;
    quantity: number;
    marketValue: number;
    price: number;
    priceChange: number;
    priceChangePct: number;
    avgPrice: number;
    totalCost: number;
    pnlDly: number;
    pnlYtd: number;
    pnlOpen: number;
    beta: number;
    lastDividend: number;
    sector: string;
    /** 1-year range, widened by the live price. */
    yearLow: number;
    yearHigh: number;
    /** The intraday bar the price falls in, or null outside the session. */
    intradayIndex: number;
    updated: string;
    updatedOn: Date;
};

export type PositionKey = {
    portfolioId: number;
    securityId: number;
};

export type Deposit = {
    id: number;
    asOfDate?: any;
    portfolioId: number;
    amount?: number;
    notes?: string;
};

export type Security = {
    id: number;
    type: SecurityTypeEnum;
    subType: SecuritySubTypeEnum;
    name: string;
    ticker: string;
    sector: string;
    industry: string;
    exchange: string;
    lastDividend: number;
    beta: number;
    currency: string;
    country: string;
    timestamp: any;
    updatedBy: string;
    updated: string;
    updatedOn: Date;
};

export type Company = {
    securityId: number;
    ticker: string;
    description: string;
    ceo: string;
    logoUrl: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    employees: number;
};

export type NewsItem = {
    id: number;
    ticker: string;
    headline: string;
    tokenizedHeadline: NewsToken[];
    source: 'B' | 'R' | 'C' | 'Y' | 'M';
};

type NewsToken = {
    type: string;
    text: string;
};

export type WatchList = {
    id: number;
    name: string;
    items: string[];
};

export type WatchListUpdate = {
    ticker: string;
    securityName: string;
    price: number;
    priceChange: number;
    priceChangePct: number;
    // Live - sent with every update, null when there is no data
    volume: number;
    dayLow: number;
    dayHigh: number;
    yearLow: number;
    yearHigh: number;
    /** The intraday bar the price falls in, or null outside the session. */
    intradayIndex: number;
    // Daily - sent only with the snapshot, and left out of live updates
    avgVolume?: number;
    yearStartPrice?: number;
    oneYearPrice?: number;
    threeYearPrice?: number;
    fiveYearPrice?: number;
};

/** Today's prices of a ticker, or the balance of a portfolio, one point per 5-minute bar. Point 0 is the prior close. */
export type IntradaySeries = {
    ticker: string;
    portfolioId: number;
    sessionDate: string;
    prevClose: number;
    interval: string;
    start: string;
    points: number[];
};

/** Beta to SPY and Sharpe from a portfolio's daily returns over a trailing year. Null with too few days of returns. */
export type RiskMetrics = {
    /** A portfolio, or null for the non-cash portfolios added up. */
    portfolioId: number;
    asOfDate: string;
    days: number;
    beta: number;
    sharpe: number;
};

export type WatchListUpdates = {
    watchlistId: number;
    updates: WatchListUpdate[];
};

export type HistMarketPrice = {
    ticker: string;
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
};

export type StockChartSettings = {
    ticker: string;
    range: string;
    type: string;
    showVolume: boolean;
    ordinal: true;
    showNavigator: boolean;
    indicator: string;
    /** Per-indicator parameter overrides, keyed by indicator type - e.g. `{rsi: {period: 20}}`. */
    indicatorParams: Record<string, PlainObject>;
    isLinked: boolean;
};

export type PriceQuote = {
    ticker: string;
    price: number;
    priceChange: number;
    priceChangePct: number;
};

export type Workflow = {
    id: number;
    name: string;
    command: string;
    schedule: string;
    isEnabled: boolean;
    commandParams: KeyValueContainer[];
    updated: string;
    updatedBy: string;
    timestamp: any;
};

export type KeyValueContainer = {
    key: string;
    value: string;
};

export type WorkflowExecution = {
    id: number;
    workflowId: number;
    completed: boolean;
    startTime: string;
    endTime: string;
    status: string;
    errorMessage: string;
    updated: string;
    updatedBy: string;
};

export type LogEventType = 'message' | 'exception';
export type LogEvent = {
    type: LogEventType;
    id: number;
    level?: string;
    message: string;
    sourceContext?: string;
    exception?: string;
    timestamp: string;
    workflowExecutionId?: number;
};

export type ExchangeInfo = {
    isOpen: boolean;
    nextEventSec: number;
    holidays: ExchangeHoliday[];
};

export type ExchangeHoliday = {
    id: number;
    name: string;
    date: string;
};

export type FullQuote = {
    isListed: boolean;
    isCash: boolean;
    ticker: string;
    name: string;
    description: string;
    exchange: string;
    priceChange: number;
    priceChangePct: number;
    bid: number;
    ask: number;
    bidSize: number;
    askSize: number;
    open: number;
    prevClose: number;
    volume: number;
    avgVolume: number;
    dayHigh: number;
    dayLow: number;
    yearHigh: number;
    yearLow: number;
    timestamp: number;
};

export enum OrderTypeEnum {
    Market = 'Market',
    Limit = 'Limit',
    Stop = 'Stop',
    StopLimit = 'StopLimit'
}

export enum TransactionTypeEnum {
    Buy = 'Buy',
    Sell = 'Sell',
    BuyToCover = 'BuyToCover',
    SellShort = 'SellShort'
}

export enum OrderTimingEnum {
    Day = 'Day',
    DayPlus = 'DayPlus',
    GTC = 'GTC',
    EXT = 'EXT',
    FOK = 'FOK'
}

export enum OrderStatusEnum {
    Open = 'Open',
    OpenContingent = 'OpenContingent',
    CancelPending = 'CancelPending',
    OpenPartialFill = 'OpenPartialFill',
    ClosedPartialFill = 'ClosedPartialFill',
    Filled = 'Filled',
    Cancelled = 'Canceled'
}

export type Order = {
    id: number;
    orderType: OrderTypeEnum;
    orderTiming: OrderTimingEnum;
    orderStatus: OrderStatusEnum;
    transactionType: TransactionTypeEnum;
    ticker: string;
    portfolioId: number;
    transactionDate?: string;
    quantity: number;
    filledQuantity?: number;
    executionPrice: number;
    limitPrice?: number;
    bid?: number;
    ask?: number;
    mark?: number;
    estAmount?: number;
    estCommission?: number;
    estFees?: number;
    created?: string;
    updated?: string;
    fills?: OrderFill[];
};

export type OrderFill = {
    id: number;
    orderId: number;
    quantity: number;
    price: number;
    venue: string;
    created: string;
};

export type ChartSeriesPoint = {
    asOfDate: string;
    value: number;
};

export type PerformanceChartData = {
    marketValueData: number[];
    depositData: number[];
    benchmarkData: number[];
};

export type SectorExposure = {
    sector: string;
    marketValue: number;
    dailyChangePct: number;
};

export type PerformanceCalcData = {
    endOfLastYearMarketValue: number;
    currentYearDeposits: number;
    cagr: number;
    /** Beta and Sharpe of the non-cash portfolios added up. */
    risk: RiskMetrics;
    /** Beta and Sharpe of each non-cash portfolio. */
    portfolioRisk: RiskMetrics[];
};

export type RiskWindow = '1Y' | '3Y' | 'ALL';

/** A closing balance, the net deposit since the previous one, and the day's returns as fractions. */
export type RiskPoint = {
    date: string;
    balance: number;
    flow: number;
    return: number;
    benchmarkReturn: number;
    /** The daily risk-free return. */
    riskFree: number;
};

export type HoldingRisk = {
    ticker: string;
    marketValue: number;
    /** Share of the gross market value of the holdings. */
    weight: number;
    daysToLiquidate: number;
};

export type MonthlyTrades = {
    month: string;
    buys: number;
    sells: number;
};

export type DailyReturn = {
    date: string;
    return: number;
};

/**
 * Risk and return metrics of a portfolio, or of the non-cash portfolios added up, over a window - see the backend's
 * decision 0056. Returns, drawdowns and VaR are fractions. A metric is null with fewer than 60 days of returns.
 */
export type RiskAnalytics = {
    portfolioId: number;
    asOfDate: string;
    days: number;
    annualizedReturn: number;
    alpha: number;
    oneYearReturn: number;
    bestDayYtd: DailyReturn;
    worstDayYtd: DailyReturn;
    beta: number;
    correlation: number;
    volatility: number;
    sharpe: number;
    sortino: number;
    omega: number;
    calmar: number;
    maxDrawdown: number;
    ulcerIndex: number;
    var95: number;
    cvar95: number;
    skewness: number;
    kurtosis: number;
    upCapture: number;
    downCapture: number;
    trackingError: number;
    informationRatio: number;
    turnover: number;
    hhi: number;
    effectiveN: number;
    daysToLiquidate: number;
    points: RiskPoint[];
    holdings: HoldingRisk[];
    trades: MonthlyTrades[];
};

export type YoyReturnData = {
    year: number;
    returnPct: number;
    dvaReturnPct: number;
    snpReturnPct: number;
};

export type Transaction = {
    id: number;
    type: TransactionTypeEnum;
    securityId: number;
    securityName: string;
    portfolioId: number;
    ticker: string;
    transactionDate: string;
    quantity: number;
    price: number;
    updated: string;
};

export type TransactionHistory = {
    transactionDate: string;
    totalQuantity: number;
    mv: number;
    transactions: Transaction[];
};

export type AccountStructure = {
    id: number;
    userId: number;
    items: IGraphElement[];
    updated: string;
    updatedBy: string;
    timestamp: any;
};

export enum AccountStructureItemType {
    EDGE = 'Edge',
    PORTFOLIO = 'Portfolio',
    BROKERAGE = 'Brokerage',
    CUSTOM = 'Custom'
}

export type ImportResults = {
    transactions: Transaction[];
    warnings: string[];
};

export type Allocation = {
    id: number;
    ownerUserId: number;
    portfolioId: number;
    securityId: number;
    allocationPct: number;
    updated?: string;
    updatedBy?: string;
    timestamp: any;
};

export type Distribution = {
    id: number;
    ownerUserId: number;
    portfolioId: number;
    securityId: number;
    amount: number;
    action: DistributionActionEnum;
    posted: string;
    updated?: string;
    updatedBy?: string;
    timestamp?: any;
};

export enum DistributionActionEnum {
    Posted = 'Posted',
    Distributed = 'Distributed',
    Reinvested = 'Reinvested'
}

export type TransactionBatch = {
    added?: any[];
    modified?: any[];
    removed?: any[];
};

export type CashFlow = {
    asOfDate: string;
    amount: number;
};

export enum RealEstateStatusEnum {
    Active = 'Active',
    Disposed = 'Disposed'
}
export type RealEstateDeal = {
    id: number;
    userId: number;
    securityId: number;
    name: string;
    sponsor: string;
    status: RealEstateStatusEnum;
    type: string; // asset class
    objective: string;
    structure: string;
    description: string;
    taxReporting: string;
    capital: number;
    inceptionDate: string;
    loanMaturityDate?: string;
    ltv?: number;
    imageId: string;
    cashFlows?: CashFlow[];
    documentIds: number[];
    updated: string;
    updatedBy: string;
    timestamp: any;
};

export type EconomicIndicator = {
    label: string;
    value: string;
    options: EconomicIndicatorOptions;
};

export type EconomicIndicatorOptions = {
    units: 'K' | 'M' | 'T' | '%' | null;
    scaleFactor?: number;
};

export type EconomicIndicatorData = {
    date: string;
    value: number;
};

export enum EconomicEventTypeEnum {
    Economic = 'Economic',
    IPOs = 'IPOs',
    Earnings = 'Earnings',
    Splits = 'Splits',
    Dividends = 'Dividends'
}

export type EconomicEvent = PlainObject;

export enum TopTradingTypeEnum {
    Gainers = 'Gainers',
    Losers = 'Losers',
    Active = 'Active'
}

export type TopTrading = {
    ticker: string;
    name: string;
    exchange: string;
    price: number;
    change: number;
    changePct: number;
};

export enum FearAndGreedTypeEnum {
    Index,
    MarketMomentum,
    StockPriceStrength,
    StockPriceBreadth,
    PutCallOptions,
    MarketVolatility,
    SafeHavenDemand,
    JunkBondDemand
}

export type CnnFearAndGreedAggregate = {
    fearAndGreed: PlainObject;
    fearAndGreedHistorical: PlainObject;
    marketMomentumSp500: PlainObject;
    marketMomentumSp125: PlainObject;
    stockPriceStrength: PlainObject;
    stockPriceBreadth: PlainObject;
    putCallOptions: PlainObject;
    marketVolatilityVix: PlainObject;
    marketVolatilityVix50: PlainObject;
    junkBondDemand: PlainObject;
    safeHavenDemand: PlainObject;
};

export type WorldIndexQuote = {
    ticker: string;
    message: string;
    currency: string;
    price: number;
    priceChange: number;
    priceChangePct: number;
};
